import type { SearchMatch } from "./search";

// Search input + dropdown — input fires every keystroke, dropdown
// supports mouse and arrow-key navigation (↑/↓ moves the active row,
// Enter selects, Esc clears). Pulled out of hud.ts so the search DOM
// plumbing lives next to its keyboard logic.

export interface SearchHud {
  onSearchInput: (cb: (query: string) => void) => void;
  onSearchSelect: (cb: (cupId: string) => void) => void;
  setSearchResults: (query: string, matches: SearchMatch[]) => void;
  clearSearch: () => void;
}

export function attachSearchHud(): SearchHud {
  const searchInput = document.getElementById(
    "search-input",
  ) as HTMLInputElement;
  const searchClear = document.getElementById(
    "search-clear",
  ) as HTMLButtonElement;
  const searchResults = document.getElementById(
    "search-results",
  ) as HTMLElement;

  const inputSubscribers: Array<(query: string) => void> = [];
  const selectSubscribers: Array<(cupId: string) => void> = [];
  let activeRowIdx = -1;
  let lastMatches: SearchMatch[] = [];

  function fireQuery(): void {
    const q = searchInput.value;
    searchClear.hidden = q.length === 0;
    for (const cb of inputSubscribers) cb(q);
  }
  function fireSelect(cupId: string): void {
    for (const cb of selectSubscribers) cb(cupId);
  }
  function clearSearch(): void {
    searchInput.value = "";
    searchClear.hidden = true;
    closeResults();
    fireQuery();
  }
  function closeResults(): void {
    searchResults.dataset.open = "false";
    searchResults.setAttribute("aria-hidden", "true");
    activeRowIdx = -1;
  }
  function refreshActiveRow(): void {
    const rows = searchResults.querySelectorAll<HTMLElement>(".search-result");
    rows.forEach((row, i) => {
      row.dataset.active = String(i === activeRowIdx);
    });
    if (activeRowIdx >= 0 && rows[activeRowIdx]) {
      rows[activeRowIdx].scrollIntoView({ block: "nearest" });
    }
  }

  searchInput.addEventListener("input", fireQuery);
  searchInput.addEventListener("focus", () => {
    if (searchInput.value.length > 0) fireQuery();
  });
  searchInput.addEventListener("keydown", (e) => {
    if (e.key === "Escape") {
      e.stopPropagation();
      clearSearch();
      searchInput.blur();
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (lastMatches.length === 0) return;
      activeRowIdx = (activeRowIdx + 1) % lastMatches.length;
      refreshActiveRow();
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      if (lastMatches.length === 0) return;
      activeRowIdx =
        activeRowIdx <= 0 ? lastMatches.length - 1 : activeRowIdx - 1;
      refreshActiveRow();
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      if (lastMatches.length === 0) return;
      // No row selected and only one match → select it. Multi-match
      // Enter requires an explicit pick via arrow keys (or a click).
      const idx =
        activeRowIdx >= 0 ? activeRowIdx : lastMatches.length === 1 ? 0 : -1;
      if (idx === -1) return;
      fireSelect(lastMatches[idx].cup.id);
    }
  });
  searchClear.addEventListener("click", () => {
    clearSearch();
    searchInput.focus();
  });
  // Close dropdown on outside click, but keep it open while interacting
  // with the input or the results themselves.
  document.addEventListener("pointerdown", (e) => {
    const target = e.target as Node;
    if (
      target !== searchInput &&
      !searchResults.contains(target) &&
      target !== searchClear
    ) {
      closeResults();
    }
  });

  return {
    onSearchInput: (cb) => inputSubscribers.push(cb),
    onSearchSelect: (cb) => selectSubscribers.push(cb),
    setSearchResults: (query, matches) => {
      lastMatches = matches;
      activeRowIdx = -1;
      searchResults.replaceChildren();
      if (query.trim() === "") {
        closeResults();
        return;
      }
      if (matches.length === 0) {
        const empty = document.createElement("div");
        empty.className = "search-result-empty";
        empty.textContent = "No matches";
        searchResults.appendChild(empty);
      } else {
        // Top 8 — beyond that the dropdown gets visually noisy. Multi-
        // match highlighting on the shelf already covers the rest.
        for (const m of matches.slice(0, 8)) {
          const row = document.createElement("div");
          row.className = "search-result";
          row.dataset.cupId = m.cup.id;
          row.addEventListener("click", () => fireSelect(m.cup.id));

          const swatch = document.createElement("div");
          swatch.className = "search-result-swatch";
          swatch.style.background = m.cup.bodyColor;
          row.appendChild(swatch);

          const text = document.createElement("div");
          text.className = "search-result-text";
          const name = document.createElement("div");
          name.className = "search-result-name";
          name.textContent = m.cup.name;
          const meta = document.createElement("div");
          meta.className = "search-result-meta";
          const loc = [m.cup.city, m.cup.country].filter(Boolean).join(", ");
          meta.textContent = `${loc}${m.cup.year ? ` · ${m.cup.year}` : ""}`;
          text.appendChild(name);
          text.appendChild(meta);
          row.appendChild(text);

          searchResults.appendChild(row);
        }
      }
      searchResults.dataset.open = "true";
      searchResults.setAttribute("aria-hidden", "false");
    },
    clearSearch,
  };
}
